import type { Rsvp } from '@otryad/shared';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/cn';
import { texts } from '@/lib/texts';

export interface RsvpButtonsProps {
  value: Rsvp | null;
  onChange: (rsvp: Rsvp) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

const OPTIONS: ReadonlyArray<{ value: Rsvp; label: string; selected: string }> = [
  { value: 'YES', label: texts.rsvp.yes, selected: 'bg-success text-accent-fg' },
  { value: 'MAYBE', label: texts.rsvp.maybe, selected: 'bg-warning text-accent-fg' },
  { value: 'NO', label: texts.rsvp.no, selected: 'bg-danger text-white' },
];

/**
 * Три ответа на «Идёшь?» одной строкой; выбранный — цветом. Хаптику вызывает экран.
 * Выбор подсвечивается в момент нажатия, вместе с хаптикой, а не после ответа сервера:
 * визуал и отдача должны совпасть в одном кадре. Ошибка возвращает прежний ответ.
 */
export function RsvpButtons({ value, onChange, disabled, loading, className }: RsvpButtonsProps) {
  const [optimistic, setOptimistic] = useState<Rsvp | null>(null);
  const wasLoading = useRef(false);
  useEffect(() => {
    // Запрос кончился: успех уже пришёл в `value`, ошибка — возвращаем прежний ответ
    if (wasLoading.current && !loading) setOptimistic(null);
    wasLoading.current = Boolean(loading);
  }, [loading]);
  const shown = loading ? (optimistic ?? value) : value;
  return (
    <div className={cn('grid grid-cols-3 gap-2', className)} role="radiogroup" aria-busy={loading}>
      {OPTIONS.map((o) => {
        const on = shown === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled || loading}
            onClick={() => {
              setOptimistic(o.value);
              onChange(o.value);
            }}
            className={cn(
              'rounded-control pressable press-scale h-12 px-2 text-sm font-semibold select-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              on ? o.selected : 'bg-surface-2 text-fg active:brightness-110',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
